// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { formatTime } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { getID, shortenWeightUnit } from '../../utils';
import { formatNumber } from '../../utils/formatNumber';
import { Table } from '../common/table';
import ExpandableTable from '../common/table/ExpandableTable';
import { CropIdLink } from '../crop';

interface Props {
  readonly datesAndTrays: readonly CropSeedDatesAndTrays[];
}

export interface CropSeedDatesAndTrays {
  readonly crop: CropData;
  readonly time: number;
  readonly totalTrays: number;
  readonly totalSeed: number;
}

interface CropData {
  readonly id: string;
  readonly crop_name: string;
  readonly sowing_density: string;
  readonly sow_density_unit: string;
  readonly soakStatus: string;
}

export interface TraysPerCrop {
  readonly traysForCrop: number;
  readonly seedForCrop: number;
  readonly crop: CropData;
}

interface SeedTrayGroup {
  readonly totalTrays: number;
  readonly traysPerCrop: readonly TraysPerCrop[];
}

export function groupCropSeedDatesAndTrays(
  datesAndTrays: readonly CropSeedDatesAndTrays[],
): readonly { readonly time: number; readonly seedTrayGroup: SeedTrayGroup }[] {
  const sorted = [...datesAndTrays];
  sorted.sort((a, b) => a.time - b.time);
  // Group by time, then sum total trays needed at that time
  const grouped = sorted.reduce((acc, dateAndTray) => {
    const maybeExisting = acc.get(dateAndTray.time);
    if (maybeExisting === undefined) {
      acc.set(dateAndTray.time, {
        totalTrays: dateAndTray.totalTrays,
        traysPerCrop: [
          {
            traysForCrop: dateAndTray.totalTrays,
            seedForCrop: dateAndTray.totalSeed,
            crop: dateAndTray.crop,
          },
        ],
      });
    } else {
      acc.set(dateAndTray.time, {
        totalTrays: maybeExisting.totalTrays + dateAndTray.totalTrays,
        traysPerCrop: [
          ...maybeExisting.traysPerCrop,
          { traysForCrop: dateAndTray.totalTrays, seedForCrop: dateAndTray.totalSeed, crop: dateAndTray.crop },
        ],
      });
    }

    return acc;
  }, new Map<number, SeedTrayGroup>());

  return Array.from(grouped.entries()).map(([time, seedTrayGroup]) => ({ time, seedTrayGroup }));
}

export default function SeedNeededTable({ datesAndTrays }: Props) {
  const { timezone } = useUserContext();
  // Group by time, then sum total trays needed at that time
  const groups = groupCropSeedDatesAndTrays(datesAndTrays);
  const sorted = [...groups];
  sorted.sort((a, b) => a.time - b.time);

  const columns = [
    {
      id: 'date',
      label: 'Date',
    },
    {
      id: 'trays',
      label: 'Trays',
      align: 'right' as const,
    },
  ];
  const rows = sorted.map(({ time, seedTrayGroup: group }) => ({
    key: time.toString(),
    date: formatTime(time, { timezone }),
    trays: group.totalTrays,
    details: <SeedDetailsTable traysPerCrop={group.traysPerCrop} />,
  }));

  return <ExpandableTable columns={columns} rows={rows} />;
}

interface SeedDetailsTableProps {
  readonly traysPerCrop: readonly TraysPerCrop[];
}

function SeedDetailsTable({ traysPerCrop }: SeedDetailsTableProps) {
  const { language } = useUserContext();
  const trayValues: number[] = [];
  const cropValues: React.ReactElement[] = [];
  const seedValues: string[] = [];
  const seedPerTrayValues: string[] = [];
  const soakValues: string[] = [];

  // Sort traysPerCrop by crop name
  const sorted = [...traysPerCrop];
  sorted.sort((a, b) => a.crop.crop_name.localeCompare(b.crop.crop_name));

  for (const dateAndTrayCount of sorted) {
    trayValues.push(dateAndTrayCount.traysForCrop);
    cropValues.push(<CropIdLink id={getID(dateAndTrayCount.crop.id)} name={dateAndTrayCount.crop.crop_name} />);
    seedValues.push(
      `${formatNumber(dateAndTrayCount.seedForCrop, language)} ${shortenWeightUnit(dateAndTrayCount.crop.sow_density_unit)}`,
    );
    seedPerTrayValues.push(
      `${dateAndTrayCount.crop.sowing_density} ${shortenWeightUnit(dateAndTrayCount.crop.sow_density_unit)}`,
    );
    soakValues.push(dateAndTrayCount.crop.soakStatus);
  }
  const columns = [
    {
      name: 'Crop',
      values: cropValues,
    },
    {
      name: 'Trays',
      values: trayValues,
      numeric: true,
    },
    {
      name: 'Seed',
      values: seedValues,
      numeric: true,
      cellStyle: {
        paddingRight: {
          xs: 1,
          sm: 2,
          md: 0,
          '@xs': 1,
          '@sm': 2,
          '@md': 0,
        },
      },
    },
    {
      name: 'Seed Per Tray',
      values: seedPerTrayValues,
      visibleAt: 'sm' as const,
      numeric: true,
      cellStyle: {
        paddingRight: {
          xs: 1,
          sm: 2,
          md: 2,
          '@xs': 1,
          '@sm': 2,
          '@md': 2,
        },
      },
    },
    {
      name: 'Soak',
      values: soakValues,
      visibleAt: 'md' as const,
    },
  ];

  return <Table columns={columns} />;
}
